'use client';

import { useState, type FC, useEffect, Suspense, useRef, useCallback } from 'react';

import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { usePushQueryChange } from '@packages/shared/src/hooks/usePushQueryChange/usePushQueryChange';
import type { GTMEventGlycerinPagination } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { InfiniteProductGridFragmentFragmentDoc } from '@packages/gql/generated/shopping/InfiniteProductGridFragmentFragmentDoc';
import type { Device } from '@packages/themes/src/default';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { usePaging } from '../../hooks/usePaging';
import { InfiniteProductGridPageLoaderSwitch } from './InfiniteProductGrid.PageLoaderSwitch';
import { ProductListVirtualPageSkeleton } from '../ProductList/ProductList.VirtualPageSkeleton';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { InfiniteLoadingFooter } from './InfiniteLoadingFooter';
import { encodePage } from '../../queryEncoding';
import { InfiniteLoadingHeader } from './InfiniteLoadingHeader';
import { getTestId } from '../../utils/featureFlags/getTestId';
import { fewerProductsPerPage } from '../../activeFeatureFlags';
import type { ProductListVirtualPageProps } from '../ProductList/ProductList.VirtualPage.client';
import { ProductListVirtualPageMemoized } from '../ProductList/ProductList.VirtualPage.memoized';
import { ProductListGrid } from '../ProductList/ProductList.Grid';

/* GraphQL */ `
  fragment InfiniteProductGridFragment on SearchProductResult {
    ...ProductGridLoaderSwitchFragment
    ...ProductListVirtualPageFragment
    ...UsePagingFragment
    ...UseDisplayModeFragment

    summary {
      totalResultCount
    }

    items {
      listPosition
    }
  }
`;

export type InfiniteProductGridProps = Pick<
  ProductListVirtualPageProps,
  'onProductFirstBecameVisible'
> & {
  maskedResult: FragmentType<typeof InfiniteProductGridFragmentFragmentDoc>;
  device: Device;
  referrer?: string;
  /**
   * How many products are loaded at once on-demand when scrolling down
   *
   * @default 72 or whatever is defined in query param for (initial) page size */
  additionalPageSize?: number;
  VirtualPageComponent?: FC<ProductListVirtualPageProps>;
};

// TODO consider unloading pages from memory ("de-rendering" with memoized size, etc.) when they are not visible anymore, for performance
// TODO consider using a library like react-window or react-virtualized to handle the infinite scrolling and virtualization
const SKIP_PREVIOUS_PAGES_ON_FIRST_RENDER = true;

export const InfiniteProductGrid: FC<InfiniteProductGridProps> = (props) => {
  const {
    maskedResult,
    device,
    referrer,
    additionalPageSize: additionalPageSizeOverride,
    VirtualPageComponent = ProductListVirtualPageMemoized,
    onProductFirstBecameVisible,
  } = props;

  const result = unmask<typeof InfiniteProductGridFragmentFragmentDoc>(maskedResult);
  const { currentPage, pageSize: initialPageSize } = usePaging(result);
  const additionalPageSize = additionalPageSizeOverride ?? initialPageSize;
  const { displayMode } = useDisplayMode(result);

  // TODO figure out how to load the correct page, so that the remembered page data (scroll to sku) still works

  const initialPage = useRef(currentPage);

  // both one-based, not an index
  const [firstPage, setFirstPage] = useState(
    SKIP_PREVIOUS_PAGES_ON_FIRST_RENDER ? initialPage.current : 1,
  ); // NOTE currently unused, as the feature to scroll back to the previous position is not yet implemented and will be pushed to a follow-up ticket
  const [lastPage, setLastPage] = useState(initialPage.current);

  const dispatchGtmEvent = useTracking();

  const { setOutcome } = useAbTesting();

  useEffect(() => {
    // initial page is not tracked because it is not a pagination interaction
    if (lastPage <= 1) return;

    dispatchGtmEvent<GTMEventGlycerinPagination>({
      event: 'Pagination',
      PaginationData: {
        pageNumber: lastPage,
      },
    });
  }, [dispatchGtmEvent, lastPage]);

  const handleProductFirstBecameVisible = useCallback(
    ({ pageNumber, indexInPage }: { indexInPage?: number; pageNumber: number }) => {
      onProductFirstBecameVisible?.({ pageNumber, indexInPage });
    },
    [onProductFirstBecameVisible],
  );

  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  // sync page to URL when loading new pages
  useEffect(() => {
    pushQueryChange(encodePage(lastPage > 1 ? lastPage : undefined), { shallow: true });
  }, [pushQueryChange, lastPage]);

  // TODO find out why loading new page leads to infinite image requests in network tab, always same images again and again, happens only with devtools open and not when the profiler is running

  // currently gaps are not supported, so all pages between firstPage and lastPage are visible
  const visiblePages = Array.from({ length: lastPage - firstPage + 1 }).map(
    (_, pageIndex) => pageIndex + firstPage,
  );

  return (
    <>
      {SKIP_PREVIOUS_PAGES_ON_FIRST_RENDER && firstPage > 1 && (
        <InfiniteLoadingHeader
          onLoadMore={() => {
            setFirstPage((previous) => Math.max(1, previous - 1));
          }}
        />
      )}

      <ProductListGrid maskedResult={result}>
        {visiblePages.map((pageNumber) => {
          const offset = // for all pages after the first, the initialPageSize has to be taken into account, exactly once
            (pageNumber >= 2 ? initialPageSize : 0) +
            // for all pages after the second, the additionalPageSize has to be taken into account, once per preceding page excluding the first one
            (pageNumber >= 3 ? (pageNumber - 2) * additionalPageSize : 0);

          const canUseServerData =
            // The initial page should be available in the server-filled cache, and does not need to be lazy-loaded
            pageNumber === initialPage.current &&
            // if there are no items, there is only the initial page, so it can be used as well
            (result.summary.totalResultCount === 0 ||
              // Only use the cached result if it is actually the correct page
              // When using browser history this may not be the case anymore, because Next.js sidesteps server-side data fetching for history navigation
              // We check the one-based index of the first item in the result, which should match the page offset (plus one for the first product on the page)
              // Items could also be empty, so we also have to check for that
              result.items[0]?.listPosition === offset + 1);

          return canUseServerData ? (
            <VirtualPageComponent
              // NOTE: there is no other possible key for the page than the page index
              key={`page-${pageNumber}`}
              maskedResult={result}
              onProductFirstBecameVisible={handleProductFirstBecameVisible}
              pageNumber={pageNumber}
              isFirstVisiblePage
              addLCPWorkaround
            />
          ) : (
            <ErrorBoundary key={`page-${pageNumber}`}>
              <Suspense
                fallback={
                  <ProductListVirtualPageSkeleton
                    displayMode={displayMode ?? 'cards'}
                    count={additionalPageSize}
                  />
                }
              >
                <InfiniteProductGridPageLoaderSwitch
                  limit={additionalPageSize}
                  offset={offset}
                  device={device}
                  referrer={referrer}
                  VirtualPageComponent={VirtualPageComponent}
                  maskedResult={result}
                  // ProductGridProps
                  onProductFirstBecameVisible={handleProductFirstBecameVisible}
                  pageNumber={pageNumber}
                  isFirstVisiblePage={pageNumber === initialPage.current}
                />
              </Suspense>
            </ErrorBoundary>
          );
        })}
      </ProductListGrid>

      <InfiniteLoadingFooter
        seenProductCount={Math.min(
          result.summary.totalResultCount,
          initialPageSize + (lastPage - 1) * additionalPageSize,
        )}
        totalProductCount={result.summary.totalResultCount}
        onLoadMore={() => {
          setLastPage((previous) => previous + 1);

          setOutcome(getTestId(fewerProductsPerPage), { LOAD: lastPage + 1 });
        }}
      />
    </>
  );
};
